// Boot: splash/onboarding (or Continue), then the game. The 3D world module starts
// downloading immediately but is only needed after onboarding, which hides its load time.
// Dev URL params: ?seed=123  ?new=1 (ignore the save)  ?skip=1 (skip onboarding with a preset;
// also ?origin=kol ?body=male|female|anon)  ?lab=1 (character lab)

import { startPresence } from "./net/presence";
import { publishApartment } from "./net/apartments";
import { setMusicZone } from "./ui/music";
import { applyUiPrefs } from "./ui/prefs";
import { COSMETICS } from "@degen/sim/progress/progress";
import { startAiPosts } from "./net/aiPosts";
import { playMode, saveKey } from "./net/mode";
import { startWorld, targetTick } from "./net/world";
import { adFor, adImage, ads, click, impression, loadAds, startAds } from "./net/ads";
import { h, render } from "preact";
import "./styles/main.css";
import { Engine } from "@degen/sim/engine";
import { Clock } from "@degen/sim/clock";
import { presetPlayer } from "@degen/sim/presets";
import { hasSave, loadGame, saveGame } from "./saveStore";
import { dayOf, minuteOfDay } from "@degen/sim/state";
import { netWorth } from "@degen/sim/player";
import { loadRemoteConfig } from "./net/config";
import { initErrors } from "./net/errors";
import { initTelemetry, track } from "./net/telemetry";
import { restoreSession } from "./net/auth";
import { lifeSwitch, maybePromptSignIn, startCloud, submitRun, syncNow } from "./net/cloud";
import { App } from "./ui/App";
import { Onboarding } from "./ui/onboarding/Onboarding";
import { actionsToday, resetActions, connectBridge, driveInput, mapOpen, minimizePhone, moveMode, personCard, phoneOpen, playerLoc, pushSnapshot, registerWorld, setSpeed, togglePhone, zone, openApp, setDispatchHook, sharedMode, dispatch, hud, setAfterCommand, showToast, helpOpen } from "./ui/bridge";
import { AD_BOARDS } from "./world/districts";
import { endRideFor, startIsland } from "./net/island";
import { localMinute, skyMode } from "./ui/prefs";
import { resumeAmbience } from "./ui/ambience";
import { carModel } from "@degen/sim/cars/cars";
import type { Person, WorldSync } from "./render/scene";
import { runZoneAction } from "./ui/hud/ZonePrompt";
import { setInCar, setRadioNews, toggleRadio, tuneRadio } from "./ui/radio";
import { carBump } from "./ui/hud/CarHud";
import { tipOnce, welcomed } from "./ui/help";
import { money } from "@degen/shared/money";

const params = new URLSearchParams(location.search);
const uiRoot = document.getElementById("ui")!;
const worldModule = import("./render/scene");

async function boot() {
  applyUiPrefs();
  void initErrors();
  void initTelemetry();
  // Remote config (tunables + flags + season theme) must land before any engine exists.
  await loadRemoteConfig();
  void restoreSession();
  startAds(); // once per session (+ each new day); house ads when offline or unbooked
  startPresence();
  if (params.has("lab")) {
    const { CharacterLab } = await import("./ui/dev/CharacterLab");
    render(h(CharacterLab, null), uiRoot);
    return;
  }

  const seed = params.has("seed") ? Number(params.get("seed")) >>> 0 : (Math.random() * 2 ** 32) >>> 0;

  // Shared world (Phase 4): continue your world life, or start one with your single-player identity.
  if (playMode() === "shared" && !params.has("skip")) {
    const saved = hasSave() ? await loadGame().catch(() => null) : null;
    if (saved) return startGame(new Engine(saved), false, true);
    const solo = await loadGame(undefined, saveKey("solo")).catch(() => null);
    if (solo?.run) {
      const fresh = Engine.newGame(seed, { ...solo.run.input });
      fresh.dispatch({ type: "WELCOME" });
      return startGame(fresh, true, true);
    }
  }

  if (params.has("skip")) {
    const body = params.get("body");
    const preset = presetPlayer(params.get("origin") === "kol" ? "kol" : "novice", body === "male" || body === "female" ? body : "anon", seed);
    return startGame(Engine.newGame(seed, preset));
  }

  const saveExists = !params.has("new") && hasSave();
  // While the player is on the splash / onboarding, the island flies by in the background.
  worldModule
    .then((m) => {
      if (!flyover && !started) flyover = m.createFlyover(document.getElementById("world") as HTMLCanvasElement, seed);
    })
    .catch(() => {});
  render(
    h(Onboarding, {
      seed,
      hasSave: saveExists,
      onContinue: async () => {
        const saved = await loadGame().catch((e) => {
          console.warn("load failed", e);
          return null;
        });
        if (saved) startGame(new Engine(saved));
      },
      onComplete: (s, input) => {
        track("onboarding_complete", { origin: input.origin, body: input.avatar.body });
        const engine = Engine.newGame(s, input);
        const sharedLife = playMode() === "shared";
        engine.dispatch({ type: "WELCOME" }); // a command, so verified-run replays include it
        saveGame(engine.state).catch(() => {});
        startGame(engine, true, sharedLife);
      },
    }),
    uiRoot,
  );
}

let flyover: { dispose(): void } | null = null;
let started = false;

async function startGame(engine: Engine, arrival = false, shared = false) {
  started = true;
  flyover?.dispose();
  flyover = null;
  const clock = new Clock();
  connectBridge(engine, clock);
  (window as unknown as { degen: unknown }).degen = { engine, clock }; // debugging in DevTools

  const save = () => (lifeSwitch.on ? Promise.resolve() : saveGame(engine.state).catch((e) => console.warn("save failed", e)));
  startCloud(engine);
  startAiPosts(engine);
  setAfterCommand((cmd) => {
    if (["PLACE_FURNITURE", "MOVE_FURNITURE", "REMOVE_FURNITURE", "MOVE_IN", "BUY_HOUSE"].includes(cmd.type)) publishApartment(engine.state);
  });
  if (shared) {
    engine.state.shared ??= { worldId: "auto", userId: "" };
    sharedMode.value = true;
    void startWorld(engine, setDispatchHook);
  }
  track("session_start", { day: dayOf(engine.state.tick), origin: engine.state.player.origin, rank: engine.state.player.rank });
  let rankedUp = false;
  engine.on((e) => {
    if (e.type === "newDay") {
      // Local-first, then mirror to the cloud and submit the run for verification.
      void save().then(() => syncNow()).then(() => submitRun());
      track("day", { day: e.day, rank: engine.state.player.rank, netWorth: Math.round(netWorth(engine.state)), actions: actionsToday });
      resetActions();
      void loadAds();
      if (e.day >= 4) maybePromptSignIn("day3");
    } else if (e.type === "rankChange" && e.to > e.from) {
      track("rank_up", { to: e.to, day: dayOf(engine.state.tick) });
      if (!rankedUp) maybePromptSignIn("rankup");
      rankedUp = true;
    } else if (e.type === "rekt") track("rekt", { day: dayOf(engine.state.tick) });
  });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) return;
    void save().then(() => syncNow());
    track("session_hidden", { day: dayOf(engine.state.tick) });
  });

  render(h(App, null), uiRoot);

  const { createWorldView } = await worldModule;
  const view = createWorldView(document.getElementById("world") as HTMLCanvasElement, {
    player: engine.state.player.avatar,
    seed: engine.state.seed,
    onZone: (z) => (zone.value = z),
    onPerson: (id) => (personCard.value = id),
    onBillboard: (board) => {
      const ad = adFor("world.billboard");
      if (ad && AD_BOARDS.includes(board)) click(ad);
      else openApp("events"); // event / hackathon slides
    },
    onBillboardSeen: () => {
      const ad = adFor("world.billboard");
      if (ad) impression(ad);
    },
    companion: companionOf(engine),
    driveInput,
    moveMode,
    arrival,
    onRideEnd: (ride) => {
      endRideFor(ride.id); // tell them they've been dropped off
      const r = dispatch({ type: "GIVE_RIDE", accountId: `p:${ride.handle}`, meters: Math.round(ride.meters), modelId: ride.modelId });
      if (!r.ok) return;
      const d = r.data as { fare: number; tip: number; jackpot: number; followers: number; paid: number } | undefined;
      if (!d?.paid) showToast(`👋 @${ride.handle} hopped out. ${ride.meters < 120 ? "Short hop, no fare." : "They said thanks 🙏"}`);
      else if (d.jackpot) showToast(`🐳 @${ride.handle} was a WHALE: ${money(d.fare)} fare + ${money(d.tip)} tip!`);
      else showToast(`🚕 @${ride.handle} paid ${money(d.fare)}${d.tip > 0 ? ` + ${money(d.tip)} tip 🎉` : ""}${d.followers ? ` · posted your ride (+${d.followers} followers)` : ""}`);
    },
    onRideOver: () => showToast("🚗 Your ride ended. You hopped out."),
    onCrash: () => {
      carBump.value++;
      showToast("💥 Bump! Watch the traffic.");
    },
  });
  registerWorld(view);
  startIsland(engine, view); // real players, live
  // How to play: a one-time welcome card (after the ferry swoop), then one-time tips at the right moments.
  if (!welcomed()) setTimeout(() => (helpOpen.value = "welcome"), arrival ? 5200 : 1200);
  phoneOpen.subscribe((open) => open && tipOnce("phone"));
  zone.subscribe((z) => z && !z.id.startsWith("driving") && !z.id.startsWith("highway") && z.id !== "car" && tipOnce("zone"));
  playerLoc.subscribe((l) => l.driving && tipOnce("drive"));
  setTimeout(() => tipOnce("person"), 90_000);
  // Car radio: plays while you're in a vehicle; the DJ knows what's pumping.
  playerLoc.subscribe((l) => setInCar(l.driving));
  setRadioNews(() => {
    const s = engine.state;
    return Object.values(s.market.tokens)
      .filter((t) => t.phase !== "dead" && t.candles.length > 30)
      .map((t) => ({ t, ch: t.candles[t.candles.length - 1].c / t.candles[Math.max(0, t.candles.length - 61)].c - 1 }))
      .sort((a, b) => b.ch - a.ch)
      .slice(0, 2)
      .map(({ t, ch }) => (ch > 0 ? `$${t.ticker} is up ${Math.round(ch * 100)}% this hour. Who's holding? 👀` : `$${t.ticker} bleeding today. Stay safe out there.`));
  });
  // Music follows your district; outfits recolor your character.
  zone.subscribe((z) => setMusicZone(z?.id ?? null));
  let outfit = "";
  hud.subscribe((h) => {
    const o = h?.equipped.outfit ?? "";
    if (o === outfit) return;
    outfit = o;
    const base = engine.state.player.avatar;
    const col = COSMETICS.find((c) => c.id === o)?.color;
    view.setPlayerAvatar(col ? { ...base, traits: { ...base.traits, topColor: col } } : base);
  });
  // World ad placements follow the served ads (and drop instantly on the kill-switch poll).
  ads.subscribe(async () => {
    const b = adFor("world.billboard");
    const img = b?.imageUrl ? await adImage(b.imageUrl) : null;
    view.setBillboardAd(b ? { id: `${b.campaignId}:${b.creativeId}`, image: img, title: b.title, text: b.text, label: b.house ? "Degen World" : "Ad" } : null);
    const tk = adFor("world.ticker");
    view.setTickerAd(tk && !tk.house ? (tk.text || tk.title).slice(0, 60) : null);
  });
  view.sync(worldSyncOf(engine));
  (window as unknown as { degen: unknown }).degen = { engine, clock, view, dispatch }; // DevTools + E2E (same module instance as the UI)

  const mobile = matchMedia("(max-width: 700px)");
  phoneOpen.subscribe((open) => {
    view.setReducedRate(open && mobile.matches);
    // A text field left focused inside the closed phone would swallow WASD.
    if (!open && document.activeElement instanceof HTMLElement) document.activeElement.blur();
  });

  // Browsers only allow audio after a gesture: start the (opt-in) ambience on the first tap or key.
  window.addEventListener("pointerdown", resumeAmbience, { once: true });
  window.addEventListener("keydown", resumeAmbience, { once: true });
  window.addEventListener("keydown", (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === "p" || e.key === "P") togglePhone();
    else if (e.key === " ") {
      e.preventDefault();
      setSpeed(clock.speed === 0 ? 1 : 0);
    } else if (e.key === "1") setSpeed(1);
    else if (e.key === "2") setSpeed(2);
    else if (e.key === "3") setSpeed(4);
    else if (e.key === "Escape") {
      if (mapOpen.value) mapOpen.value = false;
      else if (phoneOpen.value) minimizePhone();
    } else if (e.key === "m" || e.key === "M") mapOpen.value = !mapOpen.value;
    else if ((e.key === "e" || e.key === "E") && zone.value && !phoneOpen.value) runZoneAction(zone.value.actions[0]);
    else if ((e.key === "r" || e.key === "R") && playerLoc.value.driving) toggleRadio();
    else if ((e.key === "t" || e.key === "T") && playerLoc.value.driving) tuneRadio(1);
    else if ((e.key === "h" || e.key === "H") && playerLoc.value.driving) {
      if (playerLoc.value.highway) view.leaveHighway();
      else view.enterHighway();
    }
  });

  let last = performance.now();
  let lastObserve = 0;
  let lastLoc = 0;
  // ?perf=1: record main-thread cost of each frame (sim + render submit) for budget checks.
  const perf: [number, number][] | null = params.has("perf") ? [] : null;
  if (perf) (window as unknown as { degenPerf: unknown }).degenPerf = perf;
  const frame = (now: number) => {
    const t0 = performance.now();
    // rAF timestamps can be slightly older than performance.now() on the first frame: never go backwards.
    const dt = Math.max(0, Math.min(0.25, (now - last) / 1000));
    last = now;
    // Shared worlds run on the server clock; single-player uses your speed controls.
    const ticks = shared ? Math.max(0, Math.min(64, targetTick() - engine.state.tick)) : clock.update(dt);
    if (ticks > 0) engine.advance(ticks);
    const tSim = performance.now() - t0;
    pushSnapshot();
    // The sky follows your device's local time (or the fast game clock, if you prefer it in Settings).
    view.setTime(skyMode.value === "real" ? localMinute() : minuteOfDay(engine.state.tick));
    if (now - lastObserve > 1000) {
      lastObserve = now;
      view.observe(engine.state);
      view.setCompanion(companionOf(engine));
      view.sync(worldSyncOf(engine));
    }
    if (now - lastLoc > 100) {
      lastLoc = now;
      playerLoc.value = view.player();
    }
    view.render(dt);
    if (perf && perf.push([performance.now() - t0, tSim]) > 2000) perf.shift();
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

function worldSyncOf(engine: Engine): WorldSync {
  const s = engine.state;
  const car = s.cars.owned.find((c) => c.id === s.cars.activeId);
  const m = car ? carModel(car.modelId) : null;
  const guests: Record<string, Person[]> = {};
  for (const v of s.houses.visits) {
    if (v.rewarded || s.tick < v.fromTick || s.tick >= v.toTick) continue;
    const acc = s.social.accounts[Number(v.contactId.split(":")[1])];
    // Guests in a rented starter home show up when you enter the Home building.
    if (acc) (guests[v.houseId] ??= []).push({ id: acc.id, handle: acc.handle, seed: acc.pfpSeed });
  }
  return {
    activeCar: car && m ? { id: car.id, modelId: car.modelId, color: car.color, name: `${m.make} ${m.model}` } : null,
    houses: Object.fromEntries(s.houses.owned.map((h) => [h.id, h.layout])),
    guests,
    people: { me: s.player.handle },
  };
}

function companionOf(engine: Engine) {
  const s = engine.state;
  const pet = s.pets.owned.find((p) => p.id === s.pets.companionId);
  return pet ? { kind: pet.kind, seed: pet.seed } : null;
}

boot();
